import React from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import Mixpanel from 'helpers/Mixpanel.js';
import { LeftContainer } from 'helpers/styles.js';
import { formatDate, truncateStringWithEllipsis, roundedNumber, numberWithCommas } from 'helpers/Utils.js';
import Text from '@bit/knotchinc.web-react-components.text';
import KeywordSearchLink from 'components/Global/Tag/KeywordSearchLink.jsx';
import { fetchContentMetadata } from 'components/Pages/ContentView/actions.js';
import ContentModal from 'components/Global/ContentModal/ContentModal.jsx';
import ImageCard from '@bit/knotchinc.web-react-components.image-card';
import LoadingCardContent from './LoadingCardContent.jsx';
import CardMenu from './CardMenu.jsx';
import {
  StyledTagsContainer,
  StyledEntityIcon,
  StyledEntityTitle,
  StyledEntityLink,
  StyledContentTitle,
  StyledContentIcon,
  StyledKeywordsIcon,
  StyledAnnotationText,
  StyledPublishDate,
  ContentCardContainer,
  StyledSharesContainer,
  StyledAnnotations,
  StyledAnnotationBlock,
} from './styled.js';

const VerticalBar = () =>
  <Text
    fontSize="6px"
    color="palette.5"
    display="inline-block"
    margin="2px 6px"
    verticalAlign="top"
  >
    |
  </Text>;

export class ContentCard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
      dimensions: {},
      localBookmarked: props.data.is_bookmarked,
    };
  }
  componentDidUpdate(prevProps) {
    if (prevProps.data.is_bookmarked !== this.props.data.is_bookmarked) {
      this.setLocalBookmark(this.props.data.is_bookmarked);
    }
  }

  setLocalBookmark(isBookmarked) {
    this.setState({ localBookmarked: isBookmarked });
  }

  openPopup(evt) {
    Mixpanel.track('opened content piece', { public_id: this.props.data.public_id, title: this.props.data.title });
    const { top, right, bottom, left, width, height } = this.container.getBoundingClientRect();
    // Check to make sure we didn't click a menu item, which has a forwardedRef to the container
    if (evt.target.parentNode.parentNode !== this.menuContainer && evt.target.parentNode !== this.menuContainer) {
      this.setState({ isOpen: true, dimensions: { top, right, bottom, left, width, height } });
    }

    this.props.getContentData(this.props.data.public_id);
  }

  render() {
    const {
      data,
      placeholderLoading,
      height,
      width,
    } = this.props;
    const { isOpen, dimensions, localBookmarked } = this.state;
    const {
      public_id,
      title,
      content_type: contentType,
      hero_image_url,
      page_screenshot_thumbnail_url,
      publish_date,
      keyword_tags = [],
      /* TODO: implement below once API res matches */
      brand = {},
      publisher = {},
      content_format: contentFormat,
      num_words: numWords,
      num_shares: numShares,
    } = data;
    const {
    // renames
      public_id: brandID,
      name: brandName,
      logo_url: brandLogo,
    } = brand;
    const {
    // renames
      public_id: publisherID,
      name: publisherName,
      logo_url: publisherLogo,
    } = publisher;
    const showPageScreenshot = !hero_image_url && page_screenshot_thumbnail_url;
  // const pillarURL = `/blueprint/content/${public_id}`;
    const entityURL = (entityType, entityId) => `/blueprint/${entityType}/${entityId}`;
    let topKeywords = [];

    if (keyword_tags.length) {
      topKeywords = keyword_tags.filter(keyword => typeof keyword === 'object').slice(0, 2);
    }

    const isPaidContent = contentType === 'paid';
    const imageUrl = showPageScreenshot ? page_screenshot_thumbnail_url : hero_image_url;

    return (
      <ContentCardContainer
        onClick={(e) => !this.state.open && this.openPopup(e)}
        isOpen={this.state.open}
        ref={(el) => { this.container = el; }}
        height={height || '379px'}
        width={width || 'auto'}
      >
        <ImageCard
          height="100%"
          width="100%"
          imgUrl={imageUrl}
          as="div"
          placeholderLoading={placeholderLoading}
          highlight={localBookmarked}
          LoadingContent={LoadingCardContent}
        >
          {numShares ?
            <StyledSharesContainer>
              {numberWithCommas(numShares)} SHARES
            </StyledSharesContainer> : null}
          {/* Menu Items */}
          <CardMenu
            bookmarked={localBookmarked}
            id={public_id}
            ref={(el) => { this.menuContainer = el; }}
            setLocalBookmark={bool => this.setLocalBookmark(bool)}
          />
          <LeftContainer>
            {/* Share Count */}
            {/* Entity Logos & Names */}
            <StyledEntityTitle>
              <StyledEntityIcon icon={brandLogo} as={Link} to={entityURL('brand', brandID)} />
              <StyledEntityLink as={Link} to={entityURL('brand', brandID)}>
                {`${truncateStringWithEllipsis(brandName, 28)}`}
              </StyledEntityLink>
            </StyledEntityTitle>
            { isPaidContent && (
              <React.Fragment>
                <StyledEntityTitle>
                  <StyledEntityIcon icon={publisherLogo} as={Link} to={entityURL('publisher', publisherID)} />
                  <StyledEntityLink as={Link} to={entityURL('publisher', publisherID)}>
                    {truncateStringWithEllipsis(publisherName, 28)}
                  </StyledEntityLink>
                </StyledEntityTitle>
              </React.Fragment>
            ) }
            {/* Content Title */}
            <StyledContentTitle>
              {truncateStringWithEllipsis(title, 145)}
            </StyledContentTitle>
            <StyledAnnotations>
              {/* Annotations */}
              {publish_date && (
              <StyledAnnotationBlock>
                <StyledPublishDate>{formatDate(publish_date, { relative: true })}</StyledPublishDate>
                <StyledAnnotationText type="date">{`${formatDate(publish_date, { medium: true })}`}</StyledAnnotationText>
              </StyledAnnotationBlock>
            )}
              <VerticalBar />
              {contentFormat && (
              <StyledAnnotationBlock>
                <StyledContentIcon type={contentFormat} />
                {contentFormat === 'article' ? <StyledAnnotationText>{`${roundedNumber(numWords)} words`}</StyledAnnotationText> : null}
              </StyledAnnotationBlock>
            )}
              <VerticalBar />
              { contentType && (
              <StyledAnnotationBlock>
                <StyledContentIcon type={contentType} />
                <StyledAnnotationText type="contentType">{contentType}</StyledAnnotationText>
              </StyledAnnotationBlock>
            )}
            </StyledAnnotations>
            {/* Keyword Tags */}
            { topKeywords && topKeywords.length > 0 && (
              <StyledTagsContainer>
                <StyledKeywordsIcon />
                {topKeywords.map((tag, i) => tag && (<KeywordSearchLink type="text" key={`${tag.name}-${i}`} data={tag} />))}
              </StyledTagsContainer>
            )}
          </LeftContainer>
        </ImageCard>
        {isOpen && <ContentModal
          data={data}
          isOpen={isOpen}
          dimensions={dimensions}
          close={() => this.setState({ isOpen: false })}
          bookmarked={localBookmarked}
          setLocalBookmark={bookmarked => this.setLocalBookmark(bookmarked)}
        />}
      </ContentCardContainer>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  getContentData: contentId => dispatch(fetchContentMetadata(contentId)),
});

export default connect(null, mapDispatchToProps)(ContentCard);
